import React, { useState, useEffect, useRef } from 'react';
import { Typography, Flex, InputNumber, Popover, Checkbox, Image, Alert, Button, Input, App } from 'antd';
import CustomButton from '../../../components/CustomButton/Button';
import CustomModal from "../../../components/CustomModal/CustomModal";
import { PlusCircleOutlined } from '@ant-design/icons';
import SuccessImage from "../../../assets/images/success.png";
import { formatCurrencyTwoDec } from '../../../helpers/helpers';
import { useNavigate } from 'react-router-dom';
import NoData from "../../../components/NoData/NoData";
import { useDispatch, useSelector } from 'react-redux';
import { SUCCESS_MSGS, BTN_TXT } from '../../../constants/constants';
import {
  withdraw,
  getAllEscrowDetails,
  sendWithdrawOtp,
  clearWithdrawOtpState,
} from '../../../redux/slices/WalletsSlice';
import { getBalanceByOrgId } from '../../../redux/slices/manageFundSlice';

const { Text, Title } = Typography;

const OTP_LENGTH = 6;

const Withdraw = ({ bankDetailsData, withdrawableAmt, balance, onTxnRefresh }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { message } = App.useApp();
  const { user } = useSelector((state) => state.auth);
  const {
    withdrawError,
    withdrawLoading,
    withdrawOtpLoading,
    withdrawOtpError,
    withdrawOtpData,
  } = useSelector((state) => state.wallets);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [enteredAmount, setEnteredAmount] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState(new Array(OTP_LENGTH).fill(""));
  const [seconds, setSeconds] = useState(60);
  const [isResendEnabled, setIsResendEnabled] = useState(false);
  const otpBoxReference = useRef([]);

  const isOtpFilled = otp.every((digit) => digit !== "");
  const maskedMobile = withdrawOtpData?.maskedMobile;

  useEffect(() => {
    const primaryAccount = bankDetailsData?.find((account) => account.isPrimary);
    if (primaryAccount) {
      setSelectedAccount(primaryAccount.bankAccId);
    }
  }, [bankDetailsData]);

  useEffect(() => {
    if (!otpStep || !confirmModalOpen) return undefined;
    if (seconds > 0) {
      const timerId = setTimeout(() => setSeconds(seconds - 1), 1000);
      return () => clearTimeout(timerId);
    }
    setIsResendEnabled(true);
    return undefined;
  }, [seconds, otpStep, confirmModalOpen]);

  const handleAccountChange = (bankAccId) => {
    setSelectedAccount(bankAccId);
  };

  const resetConfirmModal = () => {
    setConfirmModalOpen(false);
    setOtpStep(false);
    setOtp(new Array(OTP_LENGTH).fill(""));
    setSeconds(60);
    setIsResendEnabled(false);
    dispatch(clearWithdrawOtpState());
  };

  const content = (
    <div className='bank-account-popover-content'>
      {bankDetailsData?.map((account) => (
        <div
          key={account.bankAccId}
          className='bank-option-container'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '8px 0'
          }}
          onClick={() => handleAccountChange(account.bankAccId)}
        >
          <Checkbox
            checked={selectedAccount === account.bankAccId}
            className='bank-custom-checkbox'
          >
            <Text>{account.bankName}</Text>
          </Checkbox>
          <Text strong>{`xxxx ${account.accNum?.slice(-4)}`}</Text>
        </div>
      ))}
      <CustomButton
        variant="text"
        color="default"
        icon={<PlusCircleOutlined style={{ fontSize: 16 }} />}
        iconPosition="start"
        style={{ fontSize: 14, padding: 5, fontWeight: 500 }}
        onClick={() => navigate('/profile', { state: { value: "bank" } })}
      >
        Add New Bank
      </CustomButton>
    </div>
  );

  const withdrawSuccessContent = (
    <div className="withdraw-success-modal-container">
      <Image src={SuccessImage} alt="Withdraw-Success" preview={false} style={{ marginBottom: 30 }} />
      <Title level={4}>Thank you!</Title>
      <Text style={{ color: "#40566D" }}>
        Your withdrawal request has been successfully received
      </Text>
      <CustomButton
        type="primary"
        block
        style={{ marginTop: 30 }}
        onClick={() => navigate('/live-deals')}
      >
        Explore New Deals
      </CustomButton>
    </div>
  );

  const executeWithdraw = async (otpString) => {
    const accNum = bankDetailsData?.find((account) => account.bankAccId === selectedAccount)?.accNum;
    const payload = {
      amount: enteredAmount,
      accNum,
      otp: otpString,
    };

    const res = await dispatch(withdraw(payload));
    if (res?.payload?.statusCode === 201) {
      message.success(SUCCESS_MSGS.WITHDRAW_SUCCESS);
      resetConfirmModal();
      dispatch(getAllEscrowDetails());
      dispatch(getBalanceByOrgId(user?.orgId));
      onTxnRefresh?.();
      setWithdrawModalOpen(true);
    }
  };

  const handleSendOtp = async () => {
    const res = await dispatch(sendWithdrawOtp());
    if (res?.payload?.statusCode === 201 || res?.meta?.requestStatus === "fulfilled") {
      setOtp(new Array(OTP_LENGTH).fill(""));
      setSeconds(60);
      setIsResendEnabled(false);
      setOtpStep(true);
      message.success("OTP sent to your registered mobile number");
    } else {
      message.error(
        res?.payload?.response ||
        res?.payload?.message ||
        "Failed to send OTP"
      );
    }
  };

  const handleResendOtp = async () => {
    if (!isResendEnabled) return;
    await handleSendOtp();
  };

  const handleOtpChange = (value, index) => {
    const digit = String(value || "").replace(/\D/g, "").slice(-1);
    const newArr = [...otp];
    newArr[index] = digit;
    setOtp(newArr);

    if (digit && index < OTP_LENGTH - 1) {
      otpBoxReference.current[index + 1]?.focus();
    }
  };

  const handleBackspaceAndEnter = (e, index) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      otpBoxReference.current[index - 1]?.focus();
    }
    if (e.key === "Enter" && e.target.value && index < OTP_LENGTH - 1) {
      otpBoxReference.current[index + 1]?.focus();
    }
  };

  const handleVerifyAndWithdraw = async () => {
    if (!isOtpFilled) return;
    await executeWithdraw(otp.join(""));
  };

  const handleWithdraw = () => {
    const account = bankDetailsData?.find((item) => item.bankAccId === selectedAccount);
    if (!account) return;
    setOtpStep(false);
    setOtp(new Array(OTP_LENGTH).fill(""));
    setConfirmModalOpen(true);
  };

  const selectedAccountDetails = bankDetailsData?.find((account) => account?.bankAccId === selectedAccount);
  const isWithdrawDisabled = enteredAmount < 1 || enteredAmount > parseFloat(withdrawableAmt) || !selectedAccount;
  const showError = enteredAmount > parseFloat(withdrawableAmt);

  const confirmModalContent = otpStep ? (
    <div style={{ textAlign: "center" }}>
      <Title level={4} style={{ marginBottom: 8 }}>Verify OTP</Title>
      <Text style={{ color: "#40566D", display: "block", marginBottom: 24 }}>
        Enter the 6-digit OTP sent to your registered mobile number
        {maskedMobile ? ` ${maskedMobile}` : ""}.
      </Text>
      <Flex align="center" justify="space-between" style={{ marginBottom: 16, width: "100%" }}>
        {otp.map((digit, index) => (
          <Input
            key={index}
            value={digit}
            onChange={(e) => handleOtpChange(e.target.value, index)}
            onKeyUp={(e) => handleBackspaceAndEnter(e, index)}
            ref={(reference) => (otpBoxReference.current[index] = reference)}
            style={{ textAlign: "center", width: 44 }}
            maxLength={1}
            inputMode="numeric"
            data-testid={`otp-input-${index}`}
          />
        ))}
      </Flex>
      {(withdrawOtpError || withdrawError) && (
        <Alert
          style={{ marginBottom: 16, textAlign: "left" }}
          message={
            withdrawError?.response ||
            withdrawError?.message ||
            withdrawOtpError?.response ||
            withdrawOtpError?.message ||
            withdrawOtpError ||
            withdrawError
          }
          type="error"
          showIcon
        />
      )}
      <CustomButton
        type="primary"
        block
        onClick={handleVerifyAndWithdraw}
        disabled={!isOtpFilled}
        loading={withdrawLoading}
        style={{ marginBottom: 12 }}
      >
        {BTN_TXT.VERIFY_OTP}
      </CustomButton>
      {isResendEnabled ? (
        <Text
          onClick={handleResendOtp}
          style={{ cursor: "pointer", color: "#294DD8" }}
          data-testid="resend-otp"
        >
          {BTN_TXT.RESEND_OTP}
        </Text>
      ) : (
        <Text style={{ color: "#40566D" }}>
          Resends in {seconds} {seconds === 1 ? "second" : "seconds"}
        </Text>
      )}
      <Flex gap={8} style={{ marginTop: 20 }} justify="center">
        <CustomButton onClick={resetConfirmModal}>
          Cancel
        </CustomButton>
      </Flex>
    </div>
  ) : (
    <div>
      <Text style={{ color: "#40566D", display: "block", marginBottom: 16 }}>
        You are about to withdraw <strong>{formatCurrencyTwoDec(enteredAmount)}</strong> to{' '}
        <strong>
          {selectedAccountDetails?.bankName} ****{selectedAccountDetails?.accNum?.slice(-4)}
        </strong>.
      </Text>
      <Text style={{ color: "#40566D", display: "block", marginBottom: 24 }}>
        An OTP will be sent to your registered mobile number. The withdrawal will be processed only after successful OTP verification.
      </Text>
      {withdrawOtpError && (
        <Alert
          style={{ marginBottom: 16 }}
          message={
            withdrawOtpError?.response ||
            withdrawOtpError?.message ||
            withdrawOtpError
          }
          type="error"
          showIcon
        />
      )}
      <Flex gap={8} justify="end">
        <CustomButton onClick={resetConfirmModal}>
          Cancel
        </CustomButton>
        <CustomButton
          type="primary"
          onClick={handleSendOtp}
          loading={withdrawOtpLoading}
          data-testid="send-otp-btn"
        >
          {BTN_TXT.SEND_OTP}
        </CustomButton>
      </Flex>
    </div>
  );

  return (
    <>
      <div className='withdraw-amount-container'>
        <Flex align="center" justify="space-between" style={{ marginBottom: 30, width: "100%" }}>
          <Text style={{ color: "#40566D" }}>Withdrawable Amount</Text>
          <Text style={{ color: "#40566D", fontWeight: 600 }}>{formatCurrencyTwoDec(balance.balance)}</Text>
        </Flex>
        <Flex align="center" justify="space-between" style={{ width: "100%" }}>
          <Text style={{ color: "#40566D" }}>Enter Amount</Text>
          <InputNumber
            changeOnWheel={false}
            value={enteredAmount}
            onChange={(value) => setEnteredAmount(value)}
            formatter={(value) =>
              value
                ? `${new Intl.NumberFormat('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(Number(value))}`
                : ''
            }
            parser={(value) => value?.replace(/[^\d.]/g, '')}
            size="large"
            prefix="₹"
            variant="borderless"
            className="number-input"
            min={0}
            onKeyDown={(e) => {
              const allowedKeys = ['Backspace', 'Delete', '.', 'ArrowLeft', 'ArrowRight'];
              if (!/[\d]/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault();
              }
            }}
            precision={2}
          />
        </Flex>
        {showError && (
          <Text style={{ color: "red", fontSize: 12, marginTop: 8 }}>
            Entered amount exceeds the withdrawable amount.
          </Text>
        )}
      </div>
      <div style={{ border: "1px solid #E3EAF3", width: "100%" }} />
      <div className='bank-account-container'>
        {bankDetailsData?.length === 0 ? (
          <NoData desc="No bank account added" btnText="Add Bank Account" />
        ) : (
          <Flex align='center' justify='space-between'>
            <Text style={{ color: "#40566D", fontSize: 12 }}>
              To &nbsp;
              <Text style={{ fontSize: 14 }} strong>
                {selectedAccountDetails ? `${selectedAccountDetails.bankName} ****${selectedAccountDetails.accNum.slice(-4)}` : 'Select Account'}
              </Text>
            </Text>
            <Popover
              content={content}
              trigger="click"
              placement='bottomRight'
            >
              <Button
                variant="text"
                color="primary"
                style={{ fontSize: 14, padding: 5, fontWeight: 600, color: "#294DD8" }}
              >
                CHANGE
              </Button>
            </Popover>
          </Flex>
        )}
        {withdrawError && !confirmModalOpen && (
          <Alert style={{ marginTop: 20 }} message={withdrawError} type="error" showIcon />
        )}
        <CustomButton
          block
          color="primary"
          variant="solid"
          style={{ marginTop: 30, padding: 24 }}
          onClick={handleWithdraw}
          loading={withdrawLoading}
          disabled={isWithdrawDisabled}
        >
          Withdraw
        </CustomButton>
        <CustomModal
          open={confirmModalOpen}
          centered
          title={otpStep ? undefined : "Confirm withdrawal"}
          onCancel={resetConfirmModal}
          content={confirmModalContent}
        />
        <CustomModal
          open={withdrawModalOpen}
          centered
          onCancel={() => setWithdrawModalOpen(false)}
          content={withdrawSuccessContent}
        />
      </div>
    </>
  );
};

export default Withdraw;
